"use client"

import { useEffect, useState } from "react"
import { Button, Form, Modal, Alert, Card, Badge, Spinner, Row, Col } from "react-bootstrap"
import { PrivateAxios } from "../../environment/AxiosInstance"
import { SuccessMessage, ErrorMessage } from "../../environment/ToastMessage"
import Loader from "../../loader/Loader"
import { UserAuth } from "../auth/Auth"

function PermissionManagement() {
  const [loading, setLoading] = useState(false)
  const [modules, setModules] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [currentModule, setCurrentModule] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    permissions: [{ name: "" }],
  })

  const [showApplyPermissionModal, setShowApplyPermissionModal] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedPermissions, setSelectedPermissions] = useState([])

  const { MatchPermission, Logout } = UserAuth()

  useEffect(() => {
    fetchModulesWithPermissions()
  }, [])

  const fetchModulesWithPermissions = async () => {
    setLoading(true)
    try {
      const response = await PrivateAxios.get("all-permission")
      setModules(response.data.data)
    } catch (error) {
      ErrorMessage("Failed to fetch modules and permissions")
      if (error.response?.status === 401) {
        Logout()
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await PrivateAxios.get("user/user-list")
      setUsers(response.data.user || [])
    } catch (error) {
      ErrorMessage("Failed to fetch users")
      if (error.response?.status === 401) {
        Logout()
      }
    }
  }

  const handleApplyPermissionToUsers = async () => {
    if (!selectedUser) {
      ErrorMessage("Please select a user")
      return
    }

    if (selectedPermissions.length === 0) {
      ErrorMessage("Please select at least one permission")
      return
    }

    setLoading(true)
    try {
      const payload = {
        extraPermission: JSON.stringify(selectedPermissions),
      }

      await PrivateAxios.put(`user/update-user/${selectedUser.id}`, payload)

      SuccessMessage(`Permissions applied to ${selectedUser.name} successfully`)
      handleCloseApplyPermissionModal()
    } catch (error) {
      console.error("API Error:", error.response?.data)
      ErrorMessage(error.response?.data?.msg || "Failed to apply permissions")
      if (error.response?.status === 401) {
        Logout()
      }
    } finally {
      setLoading(false)
    }
  }

  const handleShowApplyPermissionModal = () => {
    setShowApplyPermissionModal(true)
    fetchUsers()
    setSelectedUser(null)
    setSelectedPermissions([])
  }

  const handleCloseApplyPermissionModal = () => {
    setShowApplyPermissionModal(false)
    setSelectedUser(null)
    setSelectedPermissions([])
  }

  const handleUserSelection = (user) => {
    setSelectedUser(user)

    // Load existing permissions for the selected user
    if (user && user.role && user.role.length > 0) {
      const existingPermissions = []

      user.role.forEach((roleData) => {
        if (roleData.role_id === 0 && roleData.permission_id) {
          const permissionIds = JSON.parse(roleData.permission_id)

          modules.forEach((module) => {
            module.allmodule.forEach((permission) => {
              if (permissionIds.includes(permission.id)) {
                existingPermissions.push({
                  module_id: module.id,
                  permission_id: permission.id,
                  name: permission.name,
                })
              }
            })
          })
        }
      })

      setSelectedPermissions(existingPermissions)
    } else {
      setSelectedPermissions([])
    }
  }

  const handlePermissionSelection = (moduleId, permissionId, permissionName) => {
    setSelectedPermissions((prev) => {
      const exists = prev.some((p) => p.module_id === moduleId && p.permission_id === permissionId)
      if (exists) {
        return prev.filter((p) => !(p.module_id === moduleId && p.permission_id === permissionId))
      } else {
        return [...prev, { module_id: moduleId, permission_id: permissionId, name: permissionName }]
      }
    })
  }

  const isPermissionSelected = (moduleId, permissionId) => {
    return selectedPermissions.some((p) => p.module_id === moduleId && p.permission_id === permissionId)
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handlePermissionChange = (index, value) => {
    const updatedPermissions = [...formData.permissions]
    updatedPermissions[index].name = value
    setFormData({
      ...formData,
      permissions: updatedPermissions,
    })
  }

  const addPermissionField = () => {
    setFormData({
      ...formData,
      permissions: [...formData.permissions, { name: "" }],
    })
  }

  const removePermissionField = (index) => {
    if (formData.permissions.length > 1) {
      const updatedPermissions = formData.permissions.filter((_, i) => i !== index)
      setFormData({
        ...formData,
        permissions: updatedPermissions,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      ErrorMessage("Please enter module name")
      return
    }

    const validPermissions = formData.permissions.filter((p) => p.name.trim() !== "")
    if (validPermissions.length === 0) {
      ErrorMessage("Please add at least one permission")
      return
    }

    setLoading(true)
    try {
      const submitData = {
        name: formData.name,
        permissions: validPermissions,
      }

      if (editMode) {
        await PrivateAxios.put(`update-module-permissions/${currentModule.id}`, submitData)
        SuccessMessage("Module and permissions updated successfully")
      } else {
        await PrivateAxios.post("create-module-permissions", submitData)
        SuccessMessage("Module and permissions created successfully")
      }

      fetchModulesWithPermissions()
      handleCloseModal()
    } catch (error) {
      ErrorMessage(error.response?.data?.msg || "Operation failed")
      if (error.response?.status === 401) {
        Logout()
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (module) => {
    setCurrentModule(module)
    setFormData({
      name: module.name,
      permissions:
        module.allmodule.length > 0 ? module.allmodule.map((p) => ({ id: p.id, name: p.name })) : [{ name: "" }],
    })
    setEditMode(true)
    setShowModal(true)
  }

  const handleDelete = async (moduleId) => {
    if (!window.confirm("Are you sure you want to delete this module and all its permissions?")) {
      return
    }

    setLoading(true)
    try {
      await PrivateAxios.delete(`delete-module/${moduleId}`)
      SuccessMessage("Module and permissions deleted successfully")
      fetchModulesWithPermissions()
    } catch (error) {
      ErrorMessage(error.response?.data?.msg || "Failed to delete module")
      if (error.response?.status === 401) {
        Logout()
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditMode(false)
    setCurrentModule(null)
    setFormData({ name: "", permissions: [{ name: "" }] })
  }

  const handleShowModal = () => {
    setEditMode(false)
    setFormData({ name: "", permissions: [{ name: "" }] })
    setShowModal(true)
  }

  if (!MatchPermission(["System Controller"])) {
    return (
      <div className="p-4">
        <Alert variant="danger">You don't have permission to access this page.</Alert>
      </div>
    )
  }

  return (
    <>
      {loading && <Loader />}
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Module & Permission Management</h4>
          <div>
            <Button variant="success" className="me-2" onClick={handleShowApplyPermissionModal}>
              <i className="fas fa-user-plus me-2"></i>
              Apply Permission to User
            </Button>
            <Button variant="primary" onClick={handleShowModal}>
              <i className="fas fa-plus me-2"></i>
              Add Module & Permissions
            </Button>
          </div>
        </div>

        {/* Modules Grid View */}
        <Row>
          {modules && modules.length > 0 ? (
            modules.map((module) => (
              <Col key={module.id} md={6} lg={4} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                    <h6 className="mb-0">{module.name}</h6>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(module)}>
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(module.id)}>
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <small className="text-muted">Created: {new Date(module.created_at).toLocaleDateString()}</small>
                    </div>
                    <div>
                      <strong>Permissions ({module.allmodule ? module.allmodule.length : 0}):</strong>
                      <div className="mt-2">
                        {module.allmodule && module.allmodule.length > 0 ? (
                          module.allmodule.map((permission) => (
                            <Badge key={permission.id} bg="secondary" className="me-1 mb-1">
                              {permission.name}
                            </Badge>
                          ))
                        ) : (
                          <small className="text-muted">No permissions</small>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <Alert variant="info">No modules found. Click "Add Module & Permissions" to create one.</Alert>
            </Col>
          )}
        </Row>
      </div>

      {/* Add/Edit Module Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit Module & Permissions" : "Add New Module & Permissions"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Module Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter module name (e.g., User, WorkFlow, etc.)"
                required
              />
            </Form.Group>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>Permissions *</Form.Label>
                <Button variant="outline-primary" size="sm" onClick={addPermissionField}>
                  <i className="fas fa-plus me-1"></i>
                  Add Permission
                </Button>
              </div>

              {formData.permissions.map((permission, index) => (
                <div key={index} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={permission.name}
                    onChange={(e) => handlePermissionChange(index, e.target.value)}
                    placeholder={`Permission ${index + 1} (e.g., View Users, Edit User)`}
                    className="me-2"
                  />
                  {formData.permissions.length > 1 && (
                    <Button variant="outline-danger" size="sm" onClick={() => removePermissionField(index)}>
                      <i className="fas fa-trash"></i>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : editMode ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Apply Permission to User Modal */}
      <Modal show={showApplyPermissionModal} onHide={handleCloseApplyPermissionModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Apply Permissions to Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {/* Users Selection */}
            <Col md={6}>
              <h6 className="mb-3">Select User</h6>
              <div style={{ maxHeight: "400px", overflowY: "auto" }} className="border p-3 rounded">
                {users && users.length > 0 ? (
                  users.map((user) => (
                    <div key={user.id} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        id={`user-${user.id}`}
                        checked={selectedUser?.id === user.id}
                        onChange={() => handleUserSelection(user)}
                      />
                      <label className="form-check-label" htmlFor={`user-${user.id}`}>
                        <strong>{user.name}</strong>
                        <br />
                        <small className="text-muted">{user.email}</small>
                        {user.depart && <small className="text-muted ms-2">- {user.depart.name}</small>}
                      </label>
                    </div>
                  ))
                ) : (
                  <Alert variant="info" className="mb-0">
                    No users found
                  </Alert>
                )}
              </div>
              <small className="text-muted d-block mt-2">
                Selected: {selectedUser ? `${selectedUser.name}` : "None"}
              </small>
            </Col>

            {/* Permissions Selection */}
            <Col md={6}>
              <h6 className="mb-3">Select Permissions</h6>
              <div style={{ maxHeight: "400px", overflowY: "auto" }} className="border p-3 rounded">
                {modules && modules.length > 0 ? (
                  modules.map((module) => (
                    <div key={module.id} className="mb-3">
                      <h6 className="text-primary">{module.name}</h6>
                      {module.allmodule && module.allmodule.length > 0 ? (
                        module.allmodule.map((permission) => (
                          <div key={permission.id} className="form-check ms-3 mb-1">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`permission-${module.id}-${permission.id}`}
                              checked={isPermissionSelected(module.id, permission.id)}
                              onChange={() => handlePermissionSelection(module.id, permission.id, permission.name)}
                            />
                            <label className="form-check-label" htmlFor={`permission-${module.id}-${permission.id}`}>
                              {permission.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <small className="text-muted ms-3">No permissions</small>
                      )}
                    </div>
                  ))
                ) : (
                  <Alert variant="info" className="mb-0">
                    No modules found
                  </Alert>
                )}
              </div>
              <small className="text-muted d-block mt-2">Selected: {selectedPermissions.length} permission(s)</small>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseApplyPermissionModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApplyPermissionToUsers}
            disabled={loading || !selectedUser || selectedPermissions.length === 0}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" /> Applying...
              </>
            ) : (
              "Apply Permissions"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PermissionManagement
